import React, { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Film } from "lucide-react";

export default function FilmModal({ open, onClose, film, title, resumeAt = 0, onProgress }) {
  const vref = useRef(null);
  const lastSaved = useRef(0);

  const report = () => {
    const v = vref.current;
    if (v && onProgress && v.duration) onProgress(v.currentTime, v.duration);
  };

  const handleLoaded = () => {
    const v = vref.current;
    if (v && resumeAt > 3 && resumeAt < (v.duration || Infinity) - 5) {
      v.currentTime = resumeAt;
    }
  };

  const handleTime = () => {
    const v = vref.current;
    if (!v) return;
    if (v.currentTime - lastSaved.current >= 5) {
      lastSaved.current = v.currentTime;
      report();
    }
  };

  const close = () => {
    report();
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          data-testid="film-modal"
        >
          <div className="absolute inset-0 bg-purple-deep/90 backdrop-blur-md" />
          <motion.div
            className="relative z-10 w-full max-w-4xl"
            initial={{ scale: 0.94, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-gold-pale text-[11px] uppercase tracking-[0.22em] font-bold flex items-center gap-2">
                <Film className="w-4 h-4" /> {title || "The film"}
                {resumeAt > 3 && film && <span className="text-white/60 normal-case tracking-normal font-normal">· resuming where you left off</span>}
              </p>
              <button onClick={close} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors" data-testid="film-modal-close" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {film ? (
              <video
                ref={vref}
                key={film}
                src={film}
                controls
                autoPlay
                playsInline
                preload="metadata"
                onLoadedMetadata={handleLoaded}
                onTimeUpdate={handleTime}
                onPause={report}
                onEnded={report}
                className="w-full rounded-2xl bg-black aspect-video ring-1 ring-gold/30 shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
                data-testid="film-video"
              >
                {film.includes("/media/films/") && (
                  <track
                    kind="captions"
                    srcLang="en"
                    label="English"
                    default
                    src={film.replace(/\.mp4$/, ".vtt")}
                    data-testid="film-captions"
                  />
                )}
              </video>
            ) : (
              <div className="w-full rounded-2xl aspect-video bg-purple velvet ring-1 ring-white/15 flex flex-col items-center justify-center text-center px-8" data-testid="film-placeholder">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-5">
                  <Film className="w-7 h-7 text-gold-pale" />
                </div>
                <p className="font-heading text-white text-2xl mb-2">This film is on its way</p>
                <p className="text-white/70 max-w-md">
                  The film for this module is being finished. In the meantime, the worksheet and practice room are ready for you.
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
